// Copyright 2019 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, BindingEngine, computedFrom, Disposable, PLATFORM } from "aurelia-framework";
import { Router } from "aurelia-router";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { ViewProject } from "controls/project-card/ProjectCard";
import { UpdateProjectDTO } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { BusinessUnitRepository } from "repositories/BusinessUnitRepository";
import { StorageRepository } from "repositories/StorageRepository";
import { ILicenseService } from "services/License/ILicenseService";
import { MeasurementCollectionService } from "services/Measurement/MeasurementCollectionService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

/**
 * MODULE OPERATION DESCRIPTION - ProjectsModule
 * The Projects Module lists all projects, which the current user has access permission for. Simple Project management is possible via this module.
 * Other properties that are controlled in this module are (workflowproperty or router paths):
 * - None as of yet
 */
@autoinject
export class ProjectManagementModule implements IWorkflowModuleActivate {
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private filterProjectName: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private showAllLocalMeasurements = false;

    private allProjects: ViewProject[];
    private favoriteOrder: string[] = [];
    private settingFavoriteInProgress = false;

    private isFavoritesGroupOpen = true;
    private isAllGroupOpen = true;

    private disposables: Disposable[];
    private get isServerOnline(): boolean {
        return this.serverStatusService.status === ServerStatus.Online;
    }
    private moduleInstance: IWorkflowModuleInstance;
    private isLoadingProjects: boolean;

    private availableCloudStorage: number;
    private usedCloudStorage: number;
    private get freeCloudStorage(): number {
        return this.availableCloudStorage - this.usedCloudStorage;
    }
    private get freeCloudStoragePercentage(): string {
        if (this.freeCloudStorage === undefined || this.availableCloudStorage === undefined) return "100";
        return ((this.freeCloudStorage / this.availableCloudStorage) * 100).toFixed();
    }
    private get usedCloudStoragePercentage(): string {
        if (this.usedCloudStorage === undefined || this.availableCloudStorage === undefined) return "0";
        return ((this.usedCloudStorage / this.availableCloudStorage) * 100).toFixed();
    }

    private get selectedProjectId(): string {
        if (this.projectManagementService.currentProject.item) {
            return this.projectManagementService.currentProject.item.id;
        }
        return "";
    }

    @computedFrom("allProjects", "selectedProjectId")
    private get selectedProject(): ViewProject {
        if (!this.allProjects) return;
        return this.allProjects.find((p) => p.id === this.selectedProjectId);
    }

    @computedFrom("allProjects", "filterProjectName")
    private get filteredProjects(): ViewProject[] {
        if (!this.allProjects || !this.filterProjectName) return this.allProjects;

        const filter = this.filterProjectName.toLowerCase();
        return this.allProjects.filter((p) => p.name.toLowerCase().includes(filter));
    }

    @computedFrom("filteredProjects", "favoriteOrder")
    private get favoriteProjects(): ViewProject[] {
        if (!this.filteredProjects) return [];
        const favorites = this.filteredProjects.filter((project) => project.favorited);
        return favorites.sort((a, b) => this.favoriteOrder.indexOf(a.id) - this.favoriteOrder.indexOf(b.id));
    }

    @computedFrom("filteredProjects")
    private get allProjectsForDisplay(): ViewProject[] {
        if (!this.filteredProjects) return [];
        return [...this.filteredProjects].sort((a, b) => {
            const dateA = a.created ? new Date(a.created).getTime() : 0;
            const dateB = b.created ? new Date(b.created).getTime() : 0;
            return dateB - dateA;
        });
    }

    constructor(
        private readonly projectManagementService: IProjectManagementService,
        private readonly measurementCollectionService: MeasurementCollectionService,
        private readonly storageRepository: StorageRepository,
        private readonly bindingEngine: BindingEngine,
        private readonly dialogService: DialogService,
        protected readonly licenseService: ILicenseService,
        private readonly serverStatusService: IServerStatusService,
        private readonly businessUnitRepository: BusinessUnitRepository,
        private readonly router: Router,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        // Get the total storage for this user (no need to wait for it)
        this.storageRepository.getStorageSize().then((x) => {
            this.usedCloudStorage = x;
        });
        this.licenseService
            .getActiveMembershipLicense()
            .then((license) => (this.availableCloudStorage = license?.LicenseKind?.Properties.StorageBytes));
        this.searchProjectChanged();
    }

    private async createNewProject(): Promise<void> {
        this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/NewProject/NewProjectDialog"),
                lock: true,
            })
            .whenClosed((result) => {
                if (!result.wasCancelled && result.output) {
                    this.selectProject(result.output);
                    this.searchProjectChanged();
                }
            });
    }

    private selectProject(project: ViewProject): void {
        this.showAllLocalMeasurements = false;
        this.projectManagementService.setCurrentProjectWithPlaceholder(project);
        this.moduleInstance.cancelModal();
    }

    private async setProjectFavorited(project: ViewProject, favorited: boolean): Promise<void> {
        if (this.settingFavoriteInProgress) return;

        this.settingFavoriteInProgress = true;
        const previousFavorites = project.favorited;

        try {
            await this.projectManagementService.setProjectFavorited(project.id, favorited);
            if (this.allProjects) {
                this.allProjects = this.allProjects.map((p) => (p.id === project.id ? { ...p, favorited } : p));
                // put new favorite at the top of the list or remove if unfavorited
                if (favorited) {
                    this.favoriteOrder = [project.id, ...this.favoriteOrder.filter((id) => id !== project.id)];
                } else {
                    this.favoriteOrder = this.favoriteOrder.filter((id) => id !== project.id);
                }
            }
        } catch (error) {
            project.favorited = previousFavorites;
        } finally {
            this.settingFavoriteInProgress = false;
        }
    }

    private toggleFavoritesGroup(): void {
        this.isFavoritesGroupOpen = !this.isFavoritesGroupOpen;
    }

    private toggleAllGroup(): void {
        this.isAllGroupOpen = !this.isAllGroupOpen;
    }

    private async deleteProject(project: ViewProject): Promise<void> {
        const confirmationModel: ConfirmationModel = {
            title: "Delete Project",
            text: `Are you sure you want to delete the project: '${project.name}'?`,
            confirmText: "Delete",
            operation: async () => {
                await this.projectManagementService.deleteProject(project.id);
                await this.searchProjectChanged();

                // If we deleted the selected project, just select the next one in the list
                if (project.id === this.selectedProjectId) {
                    this.selectProject(this.allProjects[0]);
                }
            },
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    private async updateProject(project: ViewProject, nameAndDescription: UpdateProjectDTO): Promise<void> {
        await this.projectManagementService.updateProject(project.id, nameAndDescription);
    }

    private async openTimelineModule(): Promise<void> {
        this.moduleInstance.createModal(MeasurementWorkflowModuleCatalog.TimelineModule).show();
    }

    public async searchProjectChanged(newFilterText?: string) {
        this.isLoadingProjects = true;
        const projectsPromise = this.projectManagementService
            .getProjects(
                newFilterText
                    ? "$filter=contains(Name,'" + newFilterText + "') or contains(Description, '" + newFilterText + "')"
                    : undefined
            )
            .readToEnd();
        // Execute both calls at the same time for slightly faster loading
        const [projects, favoritedProjectIds] = await Promise.all([
            projectsPromise,
            this.projectManagementService.getFavoriteProjectIds(),
        ]);

        // Add the information about whether the project is favorited to the list
        this.allProjects = projects as ViewProject[];
        this.allProjects.forEach((p) => {
            p.favorited = favoritedProjectIds.includes(p.id);
        });

        // Update favoriteOrder to match current favorites projects
        const currentFavorites = this.favoriteOrder.filter((id) => favoritedProjectIds.includes(id));
        const newFavorites = favoritedProjectIds.filter((id) => !currentFavorites.includes(id));
        this.favoriteOrder = [...currentFavorites, ...newFavorites];

        this.isLoadingProjects = false;

        // Scroll to the selected project if it is already loaded
        // requestAnimationFrame waits for the DOM to be rendered with the new projects, before scrolling to the selected project
        requestAnimationFrame(() => {
            this.scrollProjectIntoView(this.selectedProjectId);
        });
    }

    private scrollProjectIntoView(projectId: string): void {
        const project = document.getElementById(projectId);
        if (project) {
            project.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }
}
